import { scriptJson } from "@shared/formats/script-json";

export const THEME_PREFERENCES = ["light", "dark", "system"] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type RenderedTheme = "light" | "dark";

export const DEFAULT_THEME_PREFERENCE: ThemePreference = "light";
export const LIGHT_ONLY_PATH_PREFIXES = ["/embed", "/app", "/m", "/verify"] as const;
const LANDING_PATH = "/";
export const THEME_STORAGE_KEY = "repackify-theme";
export const THEME_CHANGE_EVENT = "repackify-theme-change";
const THEME_ATTRIBUTE = "data-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

export function isThemedPath(pathname: string): boolean {
  if (pathname === LANDING_PATH) return false;
  return !LIGHT_ONLY_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export const APP_THEME_SCRIPT = `(function(){var a=${scriptJson(LIGHT_ONLY_PATH_PREFIXES)},l=location.pathname;if(l==="${LANDING_PATH}"||a.some(function(x){return l===x||l.indexOf(x+"/")===0}))return;var p;try{p=localStorage.getItem("${THEME_STORAGE_KEY}")}catch(e){}var d=p==="dark"||(p==="system"&&window.matchMedia("${DARK_QUERY}").matches);document.documentElement.setAttribute("${THEME_ATTRIBUTE}",d?"dark":"light")})()`;

let unsavedPreference: ThemePreference | null = null;

export function isThemePreference(value: unknown): value is ThemePreference {
  return THEME_PREFERENCES.some((preference) => preference === value);
}

function readStoredPreference(): ThemePreference | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function readThemePreference(): ThemePreference {
  if (typeof window === "undefined") return DEFAULT_THEME_PREFERENCE;
  return unsavedPreference ?? readStoredPreference() ?? DEFAULT_THEME_PREFERENCE;
}

export function systemPrefersDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(preference: ThemePreference): RenderedTheme {
  if (preference === "system") return systemPrefersDark() ? "dark" : "light";
  return preference;
}

export function applyAppTheme(preference: ThemePreference): void {
  document.documentElement.setAttribute(THEME_ATTRIBUTE, resolveTheme(preference));
}

export function clearAppTheme(): void {
  document.documentElement.removeAttribute(THEME_ATTRIBUTE);
}

function storeThemePreference(preference: ThemePreference): boolean {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    return true;
  } catch {
    return false;
  }
}

export function writeThemePreference(preference: ThemePreference): void {
  unsavedPreference = storeThemePreference(preference) ? null : preference;
  applyAppTheme(preference);
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

export function readRenderedTheme(): RenderedTheme {
  return document.documentElement.getAttribute(THEME_ATTRIBUTE) === "dark" ? "dark" : "light";
}

export function subscribeRenderedTheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: [THEME_ATTRIBUTE] });
  return () => observer.disconnect();
}

export function subscribeSystemTheme(onChange: () => void): () => void {
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
